import { Component, computed, effect, EventEmitter, Input, OnDestroy, Output, signal } from '@angular/core';

import {
  BarcodeFormat,
  BrowserMultiFormatReader,
  ChecksumException,
  DecodeHintType,
  FormatException,
  NotFoundException,
} from '@zxing/library';
import {
  BarcodeValidationResult,
  BarcodeValidationStatus,
  DocumentType,
} from '../../interfaces/barcode-validation.interface';

@Component({
  selector: 'app-barcode-validator',
  standalone: true,
  template: ``,
})
export class BarcodeValidatorComponent implements OnDestroy {
  @Input() imageDataUrl: string | null = null; //Base64 data URL of the image to validate
  @Input() autoValidate: boolean = true;
  @Input() documentType: DocumentType = 'id';
  @Output() validationResult = new EventEmitter<BarcodeValidationResult>();
  @Output() validationStatusChange = new EventEmitter<BarcodeValidationStatus>();

  status = signal<BarcodeValidationStatus>(BarcodeValidationStatus.IDLE);
  isScanning = computed(() => this.status() === BarcodeValidationStatus.SCANNING);
  private codeReader: BrowserMultiFormatReader | null = null;

  constructor() {
    // Auto-validate when the image changes (if enabled)
    effect(() => {
      const imageUrl = this.imageDataUrl;
      if (imageUrl && this.autoValidate) {
        this.validateBarcode().then();
      }
    });

    effect(() => {
      this.validationStatusChange.emit(this.status());
    });
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  /**
   * Validate the current image for barcodes
   */
  async validateBarcode(): Promise<void> {
    if (!this.imageDataUrl) {
      this.emitError('INVALID_IMAGE', 'No image provided for validation');
      return;
    }

    this.status.set(BarcodeValidationStatus.SCANNING);

    try {
      const result = await this.scanImage(this.imageDataUrl);
      this.status.set(BarcodeValidationStatus.SUCCESS);
      this.validationResult.emit(result);
    } catch (error) {
      this.status.set(BarcodeValidationStatus.FAILED);
      this.handleScanError(error);
    }
  }

  /**
   * Scan image for barcodes using ZXing
   */
  private async scanImage(imageDataUrl: string): Promise<BarcodeValidationResult> {
    if (!this.codeReader) {
      // Create a hints map to specify formats to try
      const hints = new Map();

      // Specify barcode formats to look for (PDF417 is used on SA IDs and passports)
      const formats = [
        BarcodeFormat.PDF_417, // South African IDs and most passports
        BarcodeFormat.QR_CODE, // Some modern passports/IDs
        BarcodeFormat.DATA_MATRIX, // Some IDs
        BarcodeFormat.CODE_128, // Alternative formats
        BarcodeFormat.CODE_39, // Alternative formats
      ];

      hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);
      hints.set(DecodeHintType.TRY_HARDER, true); // More thorough scanning

      this.codeReader = new BrowserMultiFormatReader(hints);
    }

    // Fetch image to avoid CORS issues with canvas
    const imageAsDataUrl = await this.fetchImageAsDataUrl(imageDataUrl);

    // Create an image element from the data URL
    const img = await this.loadImage(imageAsDataUrl);

    // Decode the barcode from the image
    const result = await this.codeReader.decodeFromImageElement(img);
    const format = result.getBarcodeFormat().toString();
    const rawData = result.getText();

    return {
      isValid: true,
      barcodeFormat: format,
      rawData: rawData,
    };
  }

  /**
   * Fetch image from URL and convert to data URL to avoid CORS issues
   */
  private async fetchImageAsDataUrl(imageUrl: string): Promise<string> {
    const response = await fetch(imageUrl);

    if (!response.ok) {
      throw new Error(`IMAGE_FETCH_ERROR: HTTP ${response.status}`);
    }

    const blob = await response.blob();
    return await this.blobToDataUrl(blob);
  }

  /**
   * Convert blob to data URL
   */
  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Load image from data URL
   */
  private loadImage(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('IMAGE_LOAD_ERROR'));
      img.src = dataUrl;
    });
  }

  /**
   * Handle scanning errors
   */
  private handleScanError(error: unknown): void {
    if (error instanceof NotFoundException) {
      this.emitError(
        'NO_BARCODE_FOUND',
        'No barcode detected in image',
        'The image does not contain a readable barcode',
      );
    } else if (error instanceof ChecksumException) {
      this.emitError('INVALID_FORMAT', 'Barcode checksum failed', 'The barcode format is invalid or corrupted');
    } else if (error instanceof FormatException) {
      this.emitError('INVALID_FORMAT', 'Unsupported barcode format', 'The barcode format is not supported');
    } else {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      this.emitError('SCAN_ERROR', 'Error scanning image', errorMessage);
    }
  }

  /**
   * Emit validation error
   */
  private emitError(
    code: 'NO_BARCODE_FOUND' | 'INVALID_FORMAT' | 'SCAN_ERROR' | 'INVALID_IMAGE',
    message: string,
    details?: string,
  ): void {
    this.validationResult.emit({
      isValid: false,
      error: { code, message, details },
    });
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    if (this.codeReader) {
      this.codeReader.reset();
      this.codeReader = null;
    }
  }
}
